import GroupNodeTableIcon from "Areas/Documents/graph/groupNodeTableIcon";
import DocumentObjectTypeIcon from "Areas/SharedFeatures/documentObjectTypeIcon";
import GeometryLabel from "Areas/SharedFeatures/geometryLabel";
import NodeLabel, { getNodeName } from "Areas/SharedFeatures/nodeLabel";
import getNodeSource from "Areas/SharedFeatures/types/utils/getNodeSource";
import isDocumentMapDataGeometry from "Areas/SharedFeatures/types/utils/isDocumentMapDataGeometry";
import isDocumentMapDataPoint from "Areas/SharedFeatures/types/utils/isDocumentMapDataPoint";
import isNodeSource from "Areas/SharedFeatures/types/utils/isNodeSource";
import DataGridBase from "Components/dataGridBase";
import { ColorIndicator, TextWidget } from "Components/optionForm";
import { DivWithTooltip } from "Components/withPopover";
import pinBase64 from "Content/images/graph/pinBase64";
import pinFilledBase64 from "Content/images/graph/pinFilledBase64";
import classNames from "classnames";
import { Column } from "devextreme-react/data-grid";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import includes from "lodash/includes";
import join from "lodash/join";
import map from "lodash/map";
import some from "lodash/some";
import sortBy from "lodash/sortBy";
import sumBy from "lodash/sumBy";
import values from "lodash/values";
import React, { useCallback, useMemo, useRef } from "react";
import { v4 as uuid } from "uuid";
import COLUMN from "./column";
import locale from "./table.locale";
const COLUMN_DEFAULT_WIDTH = {
    [COLUMN.OBJECT_TYPE]: 40,
    [COLUMN.COLOR]: 45,
    [COLUMN.INPUT_LINKS_COUNT]: 45,
    [COLUMN.OUTPUT_LINKS_COUNT]: 45,
    [COLUMN.PIN_ACTION]: 45
};
export default function Table(props) {
    var _a;
    const initialGridRef = useRef(null);
    const gridRef = props.gridRef || initialGridRef;
    const textBoxRef = useRef(null);
    const innerDataKeyRef = useRef("");
    const uniqId = useMemo(() => uuid(), []);
    const popoverRefs = useRef({});
    const innerData = useMemo(() => {
        const newInnerData = props.data
            ? map(props.data, e => {
                var _a;
                return isDocumentMapDataPoint(e)
                    ? {
                        ...e,
                        Id: e.NodeId,
                        Color: (_a = e.NodeSource) === null || _a === void 0 ? void 0 : _a.Color
                    }
                    : isNodeSource(e)
                        ? {
                            ...e,
                            Id: e.NodeId
                        }
                        : e;
            })
            : undefined;
        if (textBoxRef.current && gridRef.current) {
            const key = join(sortBy(map(newInnerData, "Id")), "_");
            if (key !== innerDataKeyRef.current) {
                innerDataKeyRef.current = key;
                if (textBoxRef.current.instance.option("value"))
                    textBoxRef.current.instance.reset();
                if (gridRef.current.instance.option("searchPanel.text"))
                    gridRef.current.instance.searchByText("");
            }
        }
        return newInnerData;
    }, [props.data]);
    const hasGroupedData = useMemo(() => some(innerData, "GroupId"), [innerData]);
    const tableKey = useMemo(() => uuid(), [props.columns, hasGroupedData, innerDataKeyRef.current]);
    const handleOpenTableModal = useCallback(() => {
        props.documentId &&
            props.data &&
            props.onOpenGroupTableViewModal &&
            props.onOpenGroupTableViewModal(props.documentId, {
                NodeIds: map(props.data, "NodeId"),
                GroupName: `${locale.OBJECTS}: ${props.data.length}`
            });
    }, [props.data]);
    const colorCellRender = useCallback(({ data: cellData }) => (React.createElement(ColorIndicator, { key: cellData.Color, size: 16, bgColor: cellData.Color })), []);
    const colorHeaderCellRender = useCallback(() => React.createElement(ColorIndicator, { size: 16, bgColor: null }), []);
    const inputLinksCountHeaderCellRender = useCallback(() => React.createElement("i", { className: "icon-link-indicator-in font-h5 text-gray-400" }), []);
    const outputLinksCountHeaderCellRender = useCallback(() => React.createElement("i", { className: "icon-link-indicator-out font-h5 text-gray-400" }), []);
    const pinActionHeaderCellRender = useCallback(() => {
        const popoverRefKey = `all_${uniqId}`;
        popoverRefs.current[popoverRefKey] =
            React.createRef();
        return (React.createElement(DivWithTooltip, { popoverRef: popoverRefs.current[popoverRefKey], className: "clickable", onClick: () => {
                var _a;
                if (!props.onRowsPin)
                    return;
                const popoverRef = popoverRefs.current[popoverRefKey];
                if ((_a = popoverRef === null || popoverRef === void 0 ? void 0 : popoverRef.current) === null || _a === void 0 ? void 0 : _a.instance)
                    popoverRef.current.instance.hide();
                const groupIds = new Set();
                const nodeSources = compact(map(innerData, item => {
                    const itemNodeSource = getNodeSource(item);
                    if (!itemNodeSource || !itemNodeSource.GroupId || !itemNodeSource.PinnedToGroup)
                        return null;
                    groupIds.add(itemNodeSource.GroupId);
                    return itemNodeSource;
                }));
                nodeSources.length && props.onRowsPin(nodeSources, Array.from(groupIds), true);
            }, popoverContent: locale.UNPIN_ALL_ROWS, zIndex: props.zIndex }, props.isExporting ? (React.createElement("img", { src: pinBase64 })) : (React.createElement("i", { className: "icon-pin font-h5 text-gray-400 " }))));
    }, [props.onRowsPin, innerData, props.zIndex, props.isExporting]);
    const pinActionCellRender = useCallback(({ data: cellData }) => {
        const nodeSource = getNodeSource(cellData);
        if (!nodeSource || !nodeSource.GroupId)
            return null;
        const popoverRefKey = `item_${nodeSource.NodeId}_${uniqId}`;
        popoverRefs.current[popoverRefKey] =
            React.createRef();
        return (React.createElement(DivWithTooltip, { className: "clickable", key: `${nodeSource.NodeId}_${nodeSource.PinnedToGroup}`, popoverRef: popoverRefs.current[popoverRefKey], onClick: evt => {
                var _a;
                evt.stopPropagation();
                if (!nodeSource.GroupId || !props.onRowsPin)
                    return;
                const popoverRef = popoverRefs.current[popoverRefKey];
                if ((_a = popoverRef === null || popoverRef === void 0 ? void 0 : popoverRef.current) === null || _a === void 0 ? void 0 : _a.instance)
                    popoverRef.current.instance.hide();
                props.onRowsPin([nodeSource], [nodeSource.GroupId], Boolean(nodeSource.PinnedToGroup));
            }, popoverContent: nodeSource.PinnedToGroup ? locale.UNPIN_ROW : locale.PIN_ROW, zIndex: props.zIndex }, props.isExporting ? (React.createElement("img", { src: nodeSource.PinnedToGroup ? pinBase64 : pinFilledBase64 })) : (React.createElement("i", { className: classNames("font-h5 text-gray-400", nodeSource.PinnedToGroup ? "icon-pin" : "icon-pin-filled") }))));
    }, [props.onRowsPin, props.zIndex]);
    const objectCellRender = useCallback(({ data: cellData }) => {
        const nodeSource = getNodeSource(cellData);
        return nodeSource ? (React.createElement(NodeLabel, { imgSize: 16, data: nodeSource, documentObject: props.documentObjects ? props.documentObjects[nodeSource.DocumentObjectId] : undefined, className: "line-height-1 v-center-items", showDocumentObjectName: false, imgDefaultGrayscale: true })) : (isDocumentMapDataGeometry(cellData) && (React.createElement(GeometryLabel, { imgSize: 0, data: cellData, className: "v-center-items line-height-1 mt-0_25 mb-0_25" })));
    }, [props.documentObjects]);
    const objectTypeRender = useCallback(({ data: cellData }) => {
        const nodeSource = getNodeSource(cellData);
        const nodeSourceDocumentObject = nodeSource && props.documentObjects && props.documentObjects[nodeSource.DocumentObjectId]
            ? props.documentObjects[nodeSource.DocumentObjectId]
            : undefined;
        return nodeSource ? (React.createElement(DocumentObjectTypeIcon, { documentObjectIconId: nodeSourceDocumentObject === null || nodeSourceDocumentObject === void 0 ? void 0 : nodeSourceDocumentObject.IconId, documentObjectName: nodeSourceDocumentObject === null || nodeSourceDocumentObject === void 0 ? void 0 : nodeSourceDocumentObject.Name, showEmptyBlockIfNoIcon: true })) : (isDocumentMapDataGeometry(cellData) && (React.createElement(GeometryLabel, { imgSize: 18, data: cellData, onlyIcon: true, className: "v-center-items line-height-1 mt-0_25 mb-0_25" })));
    }, [props.documentObjects]);
    const objectCalculateCellValue = useCallback((cellData) => {
        var _a;
        return cellData.NodeId
            ? cellData.NodeName ||
                getNodeName(cellData.AttributeValues, props.documentObjects
                    ? props.documentObjects[cellData.DocumentObjectId]
                    : undefined) ||
                ((_a = cellData.DocumentObject) === null || _a === void 0 ? void 0 : _a.Name)
            : cellData.Name;
    }, [props.documentObjects]);
    const objectCellWidth = useMemo(() => {
        if (!props.wrapperWidth || !Number.isFinite(props.wrapperWidth))
            return undefined;
        const otherColumnsWidth = sumBy(values(props.columns), e => {
            if (e !== COLUMN.PIN_ACTION || hasGroupedData) {
                const width = COLUMN_DEFAULT_WIDTH[e];
                return width ? width + 1 : 0;
            }
            return 0;
        });
        return (props.wrapperWidth - otherColumnsWidth - (props.isRightSideDetailsPanel ? 32 : 0));
    }, [props.wrapperWidth, props.columns, hasGroupedData]);
    const handleFilterTextChanged = useCallback(debounce((value) => {
        var _a;
        (_a = gridRef === null || gridRef === void 0 ? void 0 : gridRef.current) === null || _a === void 0 ? void 0 : _a.instance.searchByText(value);
    }, 350), []);
    return (React.createElement("div", { className: classNames("overflow-hidden h-100 h-100-max", (props.showFilter || props.showTotal) && "grid-rows-auto-1fr", props.className, props.isRightSideDetailsPanel && "p-16-20 with-gap-20"), onMouseDown: props.onTableEventStopPropagation, onWheel: props.onTableEventStopPropagation },
        (props.showFilter || props.showTotal) && (React.createElement("div", { className: "d-flex-column with-gap-16" },
            props.showFilter && (React.createElement(TextWidget, { textBoxRef: textBoxRef, mode: "search", stylingMode: "filled", showClearButton: false, placeholder: locale.SEARCH_PLACEHOLDER, onChange: handleFilterTextChanged, height: 44, className: "w-100" })),
            React.createElement("div", { className: "caption-with-actions-panel v-center-items mt-1" },
                props.showTotal && (React.createElement("div", { className: "d-inline-block pl-0_5 pr-0_5 bg-transparent border-gray-100 text-lighton-surface-variant font-bolder font-base border-rounded-medium" }, `${locale.OBJECTS}: ${((_a = props.data) === null || _a === void 0 ? void 0 : _a.length) || ""}`)),
                React.createElement(GroupNodeTableIcon, { width: 18, height: 18, onClick: handleOpenTableModal, zIndex: props.zIndex, isExporting: props.isExporting })))),
        React.createElement(DataGridBase, { gridRef: gridRef, key: tableKey, data: innerData, keyExpr: "Id", columnResizingMode: "widget", minRowHeight: 30, highlightUpdates: false, cellTemplateRequired: false, onRowClick: props.onRowClick, allFilterPanelsOn: false, wrapperHeight: props.wrapperHeight, onContentReady: props.onContentReady, clearSelectionAfterUpdate: props.clearSelectionAfterUpdate, withPopupMessage: false, gridWidth: props.wrapperWidth, allowColumnResizing: props.allowColumnResizing, onKeyDown: props.onKeyDown, rowClassName: props.rowClassName, showBorders: props.showBorders, className: "border-rounded-lg h-100" }, useMemo(() => compact([
            includes(props.columns, COLUMN.OBJECT_TYPE) && (React.createElement(Column, { key: COLUMN.OBJECT_TYPE, dataField: "_objectType", caption: "", cellRender: objectTypeRender, cssClass: "text-overflow-unset line-height-1", dataType: "string", width: COLUMN_DEFAULT_WIDTH[COLUMN.OBJECT_TYPE], allowResizing: false })),
            includes(props.columns, COLUMN.ID) && (React.createElement(Column, { key: COLUMN.ID, dataField: "Name", caption: locale.OBJECT, cellRender: objectCellRender, calculateCellValue: objectCalculateCellValue, width: objectCellWidth, dataType: "string" })),
            includes(props.columns, COLUMN.COLOR) && (React.createElement(Column, { key: COLUMN.COLOR, dataField: "Color", caption: "", cellRender: colorCellRender, dataType: "string", width: COLUMN_DEFAULT_WIDTH[COLUMN.COLOR], cssClass: "header-row-overflow-visible line-height-1", headerCellRender: colorHeaderCellRender, allowSearch: false })),
            includes(props.columns, COLUMN.INPUT_LINKS_COUNT) && (React.createElement(Column, { key: COLUMN.INPUT_LINKS_COUNT, dataField: "InputLinksCount", caption: "", dataType: "number", alignment: "left", width: COLUMN_DEFAULT_WIDTH[COLUMN.INPUT_LINKS_COUNT], cssClass: "header-row-overflow-visible line-height-1", headerCellRender: inputLinksCountHeaderCellRender })),
            includes(props.columns, COLUMN.OUTPUT_LINKS_COUNT) && (React.createElement(Column, { key: COLUMN.OUTPUT_LINKS_COUNT, dataField: "OutputLinksCount", caption: "", dataType: "number", alignment: "left", width: COLUMN_DEFAULT_WIDTH[COLUMN.OUTPUT_LINKS_COUNT], cssClass: "header-row-overflow-visible line-height-1", headerCellRender: outputLinksCountHeaderCellRender })),
            includes(props.columns, COLUMN.PIN_ACTION) && hasGroupedData && (React.createElement(Column, { key: COLUMN.PIN_ACTION, dataField: "PinnedToGroup", caption: "", dataType: "boolean", alignment: "left", minWidth: COLUMN_DEFAULT_WIDTH[COLUMN.PIN_ACTION], width: COLUMN_DEFAULT_WIDTH[COLUMN.PIN_ACTION], cssClass: "header-row-overflow-visible line-height-1", headerCellRender: pinActionHeaderCellRender, cellRender: pinActionCellRender }))
        ]), [
            props.columns,
            props.wrapperWidth,
            objectCellWidth,
            hasGroupedData,
            objectCellRender,
            objectCalculateCellValue,
            colorCellRender,
            colorHeaderCellRender,
            inputLinksCountHeaderCellRender,
            outputLinksCountHeaderCellRender,
            pinActionHeaderCellRender,
            pinActionCellRender
        ]))));
}
Table.defaultProps = {
    columns: [
        COLUMN.OBJECT_TYPE,
        COLUMN.ID,
        COLUMN.COLOR,
        COLUMN.INPUT_LINKS_COUNT,
        COLUMN.OUTPUT_LINKS_COUNT
    ],
    wrapperHeight: "100%",
    wrapperWidth: "100%",
    showFilter: true,
    showTotal: true
};
